import type { ReactNode } from 'react';
import { useGT } from 'gt-react';
import type { InvoiceMatchGap } from '@erp/rpc';
import { SystemEntity } from '@erp/shared-types';
import type { ApprovalBlock } from '@/components/accounts-payable/shared/invoiceApprovalBlocks';

export function ApprovalBlockList({
  blocks,
  groups,
}: {
  blocks?: ApprovalBlock[];
  groups?: Array<{
    id: string;
    invoiceNumber?: string | null;
    blocks: ApprovalBlock[];
  }>;
}) {
  const gt = useGT();
  if (groups) {
    return (
      <ul className="space-y-3">
        {groups.map((invoice) => (
          <li key={invoice.id} className="space-y-1">
            <div className="text-sm font-medium">
              {invoice.invoiceNumber ||
                gt('Invoice', {
                  context: 'fallback label when an AP invoice has no number',
                })}
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {blockItems(invoice.blocks, gt)}
            </ul>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      {blockItems(blocks ?? [], gt)}
    </ul>
  );
}

function blockItems(
  blocks: ApprovalBlock[],
  gt: ReturnType<typeof useGT>,
): ReactNode[] {
  return blocks.flatMap((block) => {
    if (block.type === 'gaps') {
      return block.gaps.map((gap) => (
        <li key={gap.lineItemId}>{gapText(gap, gt)}</li>
      ));
    }
    return [<li key={blockKey(block)}>{blockText(block, gt)}</li>];
  });
}

function blockKey(block: ApprovalBlock): string {
  if (block.type === 'incomplete_summary') {
    return `summary-${block.unmatched}-${block.tentative}`;
  }
  return block.type;
}

function blockText(
  block: Exclude<ApprovalBlock, { type: 'gaps' }>,
  gt: ReturnType<typeof useGT>,
): string {
  if (block.type === 'no_lines') {
    return gt('This invoice has no line items that can be matched.', {
      context: 'reason Accept is blocked on an AP invoice',
    });
  }
  if (block.type === 'supplier') {
    return gt('A supplier is required.', {
      context: 'reason Accept is blocked on an AP invoice',
    });
  }
  if (block.type === 'invoice_number') {
    return gt('An invoice number is required.', {
      context: 'reason Accept is blocked on an AP invoice',
    });
  }
  return incompleteSummaryText(block, gt);
}

function incompleteSummaryText(
  block: Extract<ApprovalBlock, { type: 'incomplete_summary' }>,
  gt: ReturnType<typeof useGT>,
): string {
  if (block.unmatched > 0 && block.tentative > 0) {
    return gt(
      '{unmatched} of {total} lines are not matched, and {tentative} still need confirmation.',
      {
        context: 'Accept blocked because match details are still loading',
        unmatched: String(block.unmatched),
        tentative: String(block.tentative),
        total: String(block.total),
      },
    );
  }
  if (block.unmatched > 0) {
    return gt('{unmatched} of {total} lines are not matched.', {
      context: 'Accept blocked because match details are still loading',
      unmatched: String(block.unmatched),
      total: String(block.total),
    });
  }
  return gt(
    '{tentative} of {total} lines have a match that still needs to be confirmed.',
    {
      context: 'Accept blocked because match details are still loading',
      tentative: String(block.tentative),
      total: String(block.total),
    },
  );
}

function gapText(gap: InvoiceMatchGap, gt: ReturnType<typeof useGT>): string {
  const targets = formatMatchTargets(gap.missingGroups, gt);
  const reason = gapReason(gap.kind, targets, gt);
  if (gap.description) {
    return gt('Line {lineNumber}: {description} — {reason}', {
      context: 'one unmatched AP invoice line in the Accept dialog',
      lineNumber: String(gap.lineNumber),
      description: gap.description,
      reason,
    });
  }
  return gt('Line {lineNumber} — {reason}', {
    context: 'one unmatched AP invoice line in the Accept dialog',
    lineNumber: String(gap.lineNumber),
    reason,
  });
}

function gapReason(
  kind: InvoiceMatchGap['kind'],
  targets: string,
  gt: ReturnType<typeof useGT>,
): string {
  if (kind === 'needs_confirmation') {
    return gt(
      'matched to {targets}, but the match still needs to be confirmed',
      {
        context: 'why an AP invoice line is not fully matched',
        targets,
      },
    );
  }
  if (kind === 'split_unconfirmed') {
    return gt('split across charges, and not every part is confirmed', {
      context: 'why an AP invoice line is not fully matched',
    });
  }
  if (kind === 'suggested_split') {
    return gt('has a suggested split that still needs to be confirmed', {
      context: 'why an AP invoice line is not fully matched',
    });
  }
  if (targets) {
    return gt('not matched to {targets}', {
      context: 'why an AP invoice line is not fully matched',
      targets,
    });
  }
  return gt('not matched', {
    context: 'why an AP invoice line is not fully matched',
  });
}

function formatMatchTargets(
  groups: string[][],
  gt: ReturnType<typeof useGT>,
): string {
  const orWord = gt('or', {
    context:
      'joins alternative AP match targets, such as a purchase order line or a charge',
  });
  const andWord = gt('and', {
    context: 'joins AP match targets that are all required',
  });
  return groups
    .map((group) =>
      group.map((type) => matchTargetLabel(type, gt)).join(` ${orWord} `),
    )
    .join(` ${andWord} `);
}

function matchTargetLabel(type: string, gt: ReturnType<typeof useGT>): string {
  if (type === SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM) {
    return gt('a purchase order line', {
      context: 'AP invoice match target',
    });
  }
  if (type === SystemEntity.AR_SALES_ORDER_LINE_ITEM) {
    return gt('a sales order line', {
      context: 'AP invoice match target',
    });
  }
  if (type === SystemEntity.CHARGE) {
    return gt('a charge', { context: 'AP invoice match target' });
  }
  return type.toLowerCase().replaceAll('_', ' ');
}
